import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, Image as ImageIcon, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadListingPhoto, deleteFile } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

function photoToSrc(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
}

function fileIdFromUrl(url) {
  if (!url) return null;
  const m = url.match(/\/api\/files\/([^/?#]+)/);
  return m ? m[1] : null;
}

export function ListingPhotoUploader({ photos = [], onChange, listingId = null, disabled = false, maxPhotos = 10 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (photos.length + list.length > maxPhotos) {
      toast.error(`Maximal ${maxPhotos} Fotos pro Inserat`);
      return;
    }
    setUploading(true);
    const uploaded = [];
    for (const file of list) {
      if (!ALLOWED.includes(file.type)) {
        toast.error(`${file.name}: Bildtyp nicht erlaubt (JPG, PNG, WebP, GIF)`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: zu groß (max. 10MB)`);
        continue;
      }
      try {
        const res = await uploadListingPhoto(file, listingId);
        if (res?.data?.file_url) uploaded.push(res.data.file_url);
      } catch (e) {
        toast.error(`${file.name}: Upload fehlgeschlagen`);
      }
    }
    if (uploaded.length) {
      onChange?.([...(photos || []), ...uploaded]);
      toast.success(`${uploaded.length} Foto${uploaded.length === 1 ? '' : 's'} hochgeladen`);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removePhoto = async (url, idx) => {
    const fid = fileIdFromUrl(url);
    onChange?.((photos || []).filter((_, i) => i !== idx));
    if (fid) {
      try { await deleteFile(fid); } catch { /* ignore */ }
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || uploading) return;
    handleFiles(e.dataTransfer?.files);
  };

  return (
    <div data-testid="listing-photo-uploader" className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="photo-file-input"
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'
        } ${disabled || uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
        data-testid="photo-dropzone"
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin" />
            <p className="text-sm">Wird hochgeladen…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Upload className="w-8 h-8" />
            <p className="text-sm font-medium">Fotos hierher ziehen oder klicken zum Auswählen</p>
            <p className="text-xs">JPG, PNG, WebP · max. 10MB · bis zu {maxPhotos} Fotos</p>
          </div>
        )}
      </div>

      {(photos || []).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="photo-preview-grid">
          {photos.map((url, idx) => (
            <div key={idx} className="relative group aspect-square rounded-md overflow-hidden border bg-muted" data-testid={`photo-thumb-${idx}`}>
              <img
                src={photoToSrc(url)}
                alt={`Foto ${idx + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              {idx === 0 && (
                <span className="absolute top-1 left-1 text-[10px] font-semibold bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
                  Titelbild
                </span>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removePhoto(url, idx); }}
                disabled={disabled}
                className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`photo-remove-${idx}`}
                aria-label="Foto entfernen"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {(photos || []).length === 0 && !uploading && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <ImageIcon className="w-3 h-3" /> Noch keine Fotos — mindestens ein Titelbild wird empfohlen.
        </p>
      )}
    </div>
  );
}

export default ListingPhotoUploader;
